import { Component, Input, OnInit, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Targeting } from 'src/app/classes/targeting/targeting';
import {
  AiAudienceGenerationService,
  AiMessage,
  BuilderHistory,
} from 'src/app/services/ai-audience-generation/ai-audience-generation.service';
import { ConversationMessagesComponent } from './components/conversation-messages/conversation-messages.component';
import { ChatInputComponent } from './components/chat-input/chat-input.component';
import { AudiencePreviewComponent } from './components/audience-preview/audience-preview.component';
import { TitanIconComponent } from 'src/app/user-interface/titan-icon/titan-icon.component';
import { TrackerService } from 'src/app/services/sp-tracker/tracker.service';

const VALID_AUDIENCE_SCORES = ['size_too_low', 'size_low', 'size_good', 'size_perfect', 'size_high', 'size_too_high'];

@Component({
  selector: 'app-ai-audience-definition',
  templateUrl: './ai-audience-definition.component.html',
  styleUrls: ['./ai-audience-definition.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ConversationMessagesComponent,
    ChatInputComponent,
    AudiencePreviewComponent,
    TitanIconComponent
  ]
})
export class AiAudienceDefinitionComponent implements OnInit {
  // Chat state
  messages: AiMessage[] = [];
  userInput = '';
  isLoading = false;
  currentAudience: Targeting | null = null;
  currentAudienceSize: number | null = null;
  currentAudienceScore: string = 'size_good';
  currentAudienceName: string = '';
  loadingStep = '';
  history: BuilderHistory[] = [];

  @Input() onApplyAudience?: (audienceSpec: Targeting, audienceName: string) => Promise<void>;

  // Tracking
  private trackingSessionId: string;
  private sessionStartTime: number;
  private generationStartTime?: number;

  constructor(
    private aiService: AiAudienceGenerationService,
    private tracker: TrackerService,
    private ngZone: NgZone,
  ) {
    this.trackingSessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
  }

  async ngOnInit() {
    await this.trackSessionStart();
  }


  onPromptSelected(prompt: string): void {
    this.userInput = prompt;
    this.sendMessage();
  }

  onRetryMessage(prompt: string): void {
    const lastMessageIndex = this.messages.length - 1;
    if (lastMessageIndex >= 0 && this.messages[lastMessageIndex].isError) {
      this.messages.splice(lastMessageIndex, 1);
    }

    this.userInput = prompt;
    this.sendMessage();
  }

  async sendMessage() {
    if (!this.userInput.trim() || this.isLoading) {
      return;
    }

    const prompt = this.userInput.trim();
    this.userInput = '';
    this.isLoading = true;
    this.loadingStep = 'Working...';

    const userMessage: AiMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: prompt,
      timestamp: new Date()
    };
    this.messages.push(userMessage);

    const messageNumber = this.messages.filter(m => m.role === 'user').length;
    const isRetry = this.messages.length > 1 && !!this.messages[this.messages.length - 2]?.isError;
    const timeFromPreviousMessage = this.getTimeFromPreviousUserMessage();

    this.trackPromptStarted(prompt, messageNumber, isRetry, timeFromPreviousMessage);

    this.generationStartTime = Date.now();

    try {
      const result = await this.aiService.buildAudience(
        prompt,
        this.history,
        {
          onStatus: (message) => this.ngZone.run(() => { this.loadingStep = message; }),
        },
        this.trackingSessionId
      );

      this.ngZone.run(() => {
        this.messages.push({
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: result.userResponse,
          timestamp: new Date(),
          audienceSize: result.audienceSize > 0 ? result.audienceSize : undefined,
          audience: result.targeting ?? undefined,
        });

        this.history.push(
          { role: 'user', content: prompt },
          { role: 'assistant', content: result.userResponse }
        );

        if (result.targeting !== null) {
          this.currentAudience = result.targeting;
          this.currentAudienceSize = result.audienceSize;
          this.currentAudienceScore = VALID_AUDIENCE_SCORES.includes(result.score_v2) ? result.score_v2 : 'size_good';
          this.currentAudienceName = result.audience_name;
        }

        this.isLoading = false;
        this.loadingStep = 'Complete!';
      });

      this.trackGenerationCompleted(result, messageNumber);
    } catch (error) {
      const errorToLog = error instanceof Error
        ? error
        : new Error((error as any)?.error?.message || (error as any)?.message || String(error));
      (errorToLog as any).prompt = prompt;

      this.trackGenerationFailed(errorToLog, messageNumber);

      this.ngZone.run(() => {
        this.messages.push({
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          content: 'I encountered an error generating your audience. Please try again.',
          timestamp: new Date(),
          isError: true,
          retryPrompt: prompt
        });

        this.isLoading = false;
        this.loadingStep = 'Error occurred';
      });
    }
  }

  async onApplyClick() {
    if (!this.currentAudience) {
      console.warn('No audience to apply');
      return;
    }

    this.trackAudienceApplied();

    if (this.onApplyAudience) {
      await this.onApplyAudience(this.currentAudience, this.currentAudienceName);
      this.resetConversation();
    }
  }

  resetConversation() {
    this.messages = [];
    this.currentAudience = null;
    this.currentAudienceSize = null;
    this.currentAudienceScore = 'size_good';
    this.currentAudienceName = '';
    this.userInput = '';
    this.history = [];
    this.loadingStep = '';
  }

  // Tracking helper methods

  private generateSessionId(): string {
    return `ai_audience_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private async trackSessionStart(): Promise<void> {
    try {
      this.tracker.track('ai_audience_session_started', {
        session_id: this.trackingSessionId,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Failed to track session start:', error);
    }
  }

  private trackPromptStarted(
    prompt: string,
    messageNumber: number,
    isRetry: boolean,
    timeFromPreviousMessage?: number
  ): void {
    try {
      const promptType = messageNumber === 1 ? 'initial' : (isRetry ? 'retry' : 'refinement');

      this.tracker.track('ai_audience_prompt_sent', {
        session_id: this.trackingSessionId,
        message_number: messageNumber,
        prompt_length: prompt.length,
        prompt_word_count: prompt.split(/\s+/).length,
        prompt_type: promptType,
        time_from_previous_message: timeFromPreviousMessage,
        time_from_session_start: Date.now() - this.sessionStartTime
      });
    } catch (error) {
      console.error('Failed to track prompt started:', error);
    }
  }

  private trackGenerationCompleted(result: any, messageNumber: number): void {
    try {
      const flexSpecCount = result.targeting?.flex?.length || 0;

      this.tracker.track('ai_audience_generation_completed', {
        session_id: this.trackingSessionId,
        message_number: messageNumber,
        generation_duration: Date.now() - (this.generationStartTime || Date.now()),
        time_from_session_start: Date.now() - this.sessionStartTime,
        audience_size: result.audienceSize,
        audience_score: result.score_v2,
        flexible_spec_count: flexSpecCount
      });
    } catch (error) {
      console.error('Failed to track generation completed:', error);
    }
  }

  private trackGenerationFailed(error: Error, messageNumber: number): void {
    try {
      const promptType = messageNumber === 1 ? 'initial' : 'refinement';

      this.tracker.track('ai_audience_generation_failed', {
        session_id: this.trackingSessionId,
        message_number: messageNumber,
        generation_duration: Date.now() - (this.generationStartTime || Date.now()),
        time_from_session_start: Date.now() - this.sessionStartTime,
        error_message: error.message,
        prompt_type: promptType,
      });
    } catch (trackingError) {
      console.error('Failed to track generation failed:', trackingError);
    }
  }

  private trackAudienceApplied(): void {
    try {
      if (!this.currentAudience || !this.currentAudienceSize) {
        return;
      }

      const userMessages = this.messages.filter(m => m.role === 'user');
      const totalMessages = this.messages.length;
      const refinementIterations = userMessages.length - 1;

      const lastAssistantMessage = [...this.messages].reverse().find(m => m.role === 'assistant' && !m.isError);
      const timeFromLastGeneration = lastAssistantMessage
        ? Date.now() - lastAssistantMessage.timestamp.getTime()
        : 0;

      this.tracker.track('ai_audience_applied', {
        session_id: this.trackingSessionId,
        total_messages: totalMessages,
        user_messages: userMessages.length,
        refinement_iterations: refinementIterations,
        time_to_apply: Date.now() - this.sessionStartTime,
        time_from_last_generation: timeFromLastGeneration,
        audience_size: this.currentAudienceSize,
        audience_score: this.currentAudienceScore,
      });
    } catch (error) {
      console.error('Failed to track audience applied:', error);
    }
  }

  private getTimeFromPreviousUserMessage(): number | undefined {
    const userMessages = this.messages.filter(m => m.role === 'user');
    if (userMessages.length < 2) {
      return undefined;
    }

    const lastUserMessage = userMessages[userMessages.length - 2];
    return Date.now() - lastUserMessage.timestamp.getTime();
  }
}
